/*
 * ESte archivo es temporal. hasta validar si la obtencion de datos es correcta, se eliminara si asi lo quieren.
 * Detalle técnico de cada bloque del dashboard de rendimientos (icono «i»).
 * Texto plano y sin traducir a propósito: es documentación para desarrollo y para enseñar al cliente
 * cómo sale cada dato. Al darlo por bueno se puede borrar este fichero y los <info-button>.
 *
 * Referencias usadas en todos los textos:
 *   Servicio: app/Models/Services/DashboardPerformance/PerformanceDashboardService.php
 *   API:      GET /api/v2/project/{project}/dashboard-performance/activities?week_start=&week_end=
 *   Corte:    hoy (Carbon::today()); la semana es la semana del proyecto que contiene hoy.
 *   Medición física (assignments): type = 'App\Models\AssignmentType\Resource', assignable_type = 'App\Models\Activity',
 *             total_units > 0, unidad (measure_units.code) distinta de '%', variant_type no 11 ni 14,
 *             y sin padre Resource (parent_assignment_id nulo o padre de otro tipo, p. ej. CertifierHead).
 *   Línea OK: assignment_lines.status = 0 y deleted_at nulo.
 *   Jornada:  projects.configs.man_hour (8 si no existe). Días laborables: projects.configs.work_days + festivos del calendario.
 *   Rama:     la actividad padre (parent_id nulo) y todas sus subactividades; se quitan las subactividades con status = 6
 *             (canceladas) y las copias con el mismo planning_code dentro del mismo padre (se queda el id menor).
 */

export const PERFORMANCE_INFO_TITLE = 'Cómo se calcula'

export const PERFORMANCE_INFO_SECTIONS = {
  summary: 'En pocas palabras',
  detail: 'Detalle técnico',
}

/*
 * Explicación general de cada bloque, para quien no conoce el sistema por dentro.
 * Mismas claves que PERFORMANCE_INFO; se muestra antes del detalle técnico.
 */
export const PERFORMANCE_INFO_SUMMARY = {
  cutoff: `Es el día al que se refieren todos los números de la pantalla: hoy.
Todo lo que se ve (avance, ritmos, horas, retrasos) está calculado hasta esa fecha.
La semana que aparece arriba es la semana de la obra en la que cae ese día.`,

  selection: `No se revisan todas las actividades, solo las importantes de la semana.
Una actividad entra si se está trabajando esta semana, si se puede medir en unidades reales
(metros, kilos, toneladas…) y si cumple al menos una condición:
- está en la ruta crítica (si se retrasa, se retrasa la obra);
- pesa mucho en el coste total de la obra;
- su presupuesto ha cambiado bastante respecto al inicial.`,

  reasons: `Muestra, actividad por actividad, por qué está en la lista.
En verde aparece la condición que cumple. Con una basta.`,

  scope: `Resume cuántas actividades se están revisando y qué parte del presupuesto de la obra representan.`,

  kpi_max_delay: `El mayor retraso que se espera en la obra, en días, y qué actividad lo provoca.
Se calcula comparando lo que falta por hacer con el ritmo al que se ha trabajado el último mes.
Si una actividad no tiene datos suficientes, no se puede calcular y se indica abajo.`,

  kpi_overdue_actions: `Cuenta las acciones acordadas para corregir problemas cuya fecha límite ya pasó y que todavía no están terminadas.`,

  kpi_critical_path: `Cuántas de las actividades revisadas están en la ruta crítica, es decir,
que cualquier retraso en ellas retrasa el final de la obra.`,

  kpi_priority: `Cuántas actividades se han marcado como prioridad P1 o P2.
La prioridad la decide el equipo en la ficha de cada actividad.`,

  chart_progress: `Para cada actividad compara tres porcentajes de avance a día de hoy:
- Presupuestado: lo que debería llevar según el plan original.
- Planificado: lo que debería llevar según el último plan.
- Real: lo que realmente lleva.
Si el real está por debajo de los otros dos, la actividad va atrasada.`,

  chart_staff: `Cuántas personas, de media, se planificaron y cuántas han trabajado realmente en cada actividad desde que empezó.`,

  chart_weeks: `Cuántas semanas harían falta para terminar lo que falta en cada actividad:
- al ritmo real del último mes;
- al ritmo que marca el plan para las próximas semanas.
La raya roja son las semanas que quedan hasta la fecha de fin. Si la barra la supera, no se llega a tiempo.`,

  attention: `La tabla principal: una fila por actividad revisada, ordenadas de más a menos retraso.
Reúne avance, lo que falta, ritmos, fechas, retraso, estado y la acción acordada.
Al pulsar una fila se abre su ficha más abajo.`,

  sheet_actual_progress: `Cuánto lleva hecho la actividad, comparado con lo que debería llevar según el plan original y según el último plan.`,

  sheet_project_delay: `Cuántos días se retrasaría el final de la obra por esta actividad si se sigue trabajando al ritmo del último mes.`,

  sheet_weeks_to_end: `Semanas que quedan hasta la fecha de fin prevista y cuánto falta por hacer.
Si sale en negativo, la fecha de fin ya pasó.`,

  sheet_actual_yield: `Horas de trabajo que se necesitan para hacer una unidad (un metro, un kilo…).
Cuanto más bajo, más productivo. Se compara con lo que se había planificado.`,

  past: `Resumen desde el inicio de la actividad hasta hoy: cuánto se ha avanzado, con cuánta gente
y con qué productividad, frente a lo planificado.`,

  future: `Qué hace falta para terminar a tiempo:
- a qué ritmo hay que trabajar cada semana;
- cuántas personas harían falta;
- qué productividad hay que conseguir.
Se compara con lo que dice el plan y con lo que se ha conseguido el último mes.`,

  situation: `Estado actual de la actividad (en curso, atrasada, completada…), que se calcula solo,
y la causa del problema, que escribe el equipo.`,

  weekly_progress: `Cómo ha ido creciendo el avance acumulado en las últimas 6 semanas: lo previsto frente a lo real.`,

  weekly_staff: `Cuántas personas había planificadas y cuántas han trabajado en cada una de las últimas 6 semanas.`,

  weekly_yield: `Horas por unidad en cada una de las últimas 6 semanas: lo planificado frente a lo real.`,

  history_progress: `El avance acumulado mes a mes desde que empezó la actividad.
La línea «Necesario» muestra cómo tendría que avanzar a partir de hoy para terminar en su fecha.`,

  history_staff: `Personas por mes: planificadas, reales y, en los meses que quedan, las que harían falta para llegar a tiempo.`,

  history_yield: `Horas por unidad mes a mes: planificadas, reales y, en los meses que quedan, las necesarias para llegar a tiempo.`,

  monthly: `La misma información mes a mes, en tabla: cantidad hecha, avance acumulado, personas y productividad.
Si la actividad dura más de un año, solo se ven los últimos 12 meses.`,

  system_kpis: `Resumen de la medición elegida: avance, lo que falta, cuánto tardaría en terminarse,
el retraso que provoca y cuántas personas harían falta para acabar a tiempo.`,

  system_measurements: `Las cosas que se miden en la actividad (metros de cable, kilos de soporte…), cada una por separado,
porque no se pueden sumar unidades distintas.
Si varias partes de la actividad miden lo mismo, se juntan en una sola fila.`,

  system_series: `Semana a semana (o mes a mes), cuánto estaba previsto hacer, cuánto se hizo y cuántas horas se trabajaron.`,

  simulation: `Una calculadora para probar escenarios: se cambian los valores (lo que falta, el ritmo, la fecha de fin…)
y se ve al momento cuántas semanas harían falta, qué retraso habría y cuántas personas se necesitarían.
No guarda nada.`,

  agreed_action: `Aquí el equipo apunta qué se va a hacer para corregir el problema, quién se encarga, para cuándo y en qué estado está.`,
}

export const PERFORMANCE_INFO = {
  cutoff: {
    title: 'Fecha de corte',
    text: `Fecha de corte = hoy (fecha del servidor al llamar a la API).

Semana del proyecto (cabecera "Semana X de Y"):
- Semana 1: desde projects.current_start_date hasta ese domingo.
- Resto: lunes a domingo; la última se recorta en projects.current_end_date.
- Es el mismo cálculo que la planificación semanal (weeklyProjectWeeks.js).

El frontend manda week_start / week_end de esa semana. Si hoy es posterior al fin del proyecto se usa la última semana.

Los % de planificación son los vigentes: no hay cortes históricos guardados, se recalculan cada vez con las fechas actuales.`,
  },

  selection: {
    title: 'Selección de actividades',
    text: `1) Candidatas (summary.candidates):
   activities del proyecto con parent_id NULL y cuyo rango actual solapa la semana:
   current_start_date <= week_end AND current_end_date >= week_start.

2) Medibles (summary.measurable):
   candidatas cuya rama tiene al menos una medición física (ver cabecera del fichero:
   Resource, total_units > 0, unidad distinta de %, sin padre Resource).
   "Sin medición física válida" = candidatas − medibles.

3) Seleccionadas: medibles que cumplen al menos UNO de estos criterios:
   - Ruta crítica: activities.critical_path >= 1 (0 o NULL = no).
   - Peso: coste de la rama / coste total del proyecto × 100 >= 5 %.
   - Desviación de la partida: |coste − coste inicial| / coste inicial × 100 > 10 %.
   (Riesgo identificado: no hay datos en el sistema, no se evalúa.)

Coste (getExecutionCostByActivity), solo mediciones Resource sin hijos:
   coste         = Σ total_units × unit_price
   coste inicial = Σ COALESCE(total_units_initial, total_units) × unit_price
   Coste total del proyecto = suma del coste de todas sus actividades (summary.total_cost).

Para probar:
- Poner activities.critical_path = 1 en una actividad padre de la semana con medición física → entra por ruta crítica.
- Dar unit_price a sus mediciones hasta que su coste sea >= 5 % del total → entra por peso.
- Poner total_units_initial distinto de total_units (más de un 10 %) y unit_price > 0 → entra por desviación.
- Mover current_start_date / current_end_date fuera de la semana → deja de ser candidata.`,
  },

  reasons: {
    title: 'Motivo por actividad',
    text: `Una fila por actividad seleccionada (mismo orden que la tabla de atención).

Ruta crítica: activities.critical_path >= 1 → "Sí" en verde.
% del coste:  cost_share = coste de la rama / summary.total_cost × 100. Verde si >= 5 %.
Desviación:   cost_deviation = (coste − coste inicial) / coste inicial × 100.
              Verde si |valor| > 10 %. "—" si el coste inicial es 0 (no hay unit_price o cantidades).
Motivo:       frase armada con los criterios que cumple (reasons[] de la API: critical_path, weight, cost_deviation).

Basta con un criterio en verde para que la actividad esté seleccionada.

Para probar: ver la info de "Selección de actividades".`,
  },

  scope: {
    title: 'Alcance de la selección',
    text: `- Seleccionadas: summary.selected.
- "de N": summary.candidates (actividades padre que solapan la semana).
- Canceladas excluidas: summary.cancelled_ignored = subactividades de las ramas seleccionadas con activities.status = 6.
- Presupuesto cubierto: summary.selected_cost = Σ coste de las actividades seleccionadas.
- % del total: selected_cost / total_cost × 100.

Si ninguna medición tiene unit_price, el coste es 0 y el % sale "—".`,
  },

  kpi_max_delay: {
    title: 'Retraso máx. del proyecto',
    text: `Valor: el mayor project_delay de las actividades seleccionadas; debajo, el código y el nombre de esa actividad.

project_delay (de la medición primaria):
   semanas al ritmo real = pendiente / ritmo real de 4 semanas
   semanas que quedan    = (current_end_date − hoy) / 7
   retraso bruto         = max(0, redondeo((semanas al ritmo real − semanas que quedan) × 7)) días
   holgura               = 0 si critical_path >= 1; si no, NULL (P6 no trae la holgura)
   retraso del proyecto  = max(0, retraso bruto − holgura); solo si existen las dos.

Pie: "Parcial: X/Y sin dato" = actividades sin project_delay / seleccionadas.

Para que salga un valor, la actividad necesita:
- critical_path = 1,
- medición primaria,
- líneas OK con start_date en las últimas 4 semanas (ritmo real > 0),
- current_end_date.`,
  },

  kpi_overdue_actions: {
    title: 'Acciones vencidas',
    text: `Sale de la tabla activity_performance_reviews (lo que se guarda en "Acción acordada").

- Acciones registradas: actividades seleccionadas con el campo action no vacío.
- Vencidas: de esas, action_date < hoy y action_status distinto de 'completed'.

Solo cuentan las actividades seleccionadas de la semana: una acción de una actividad que hoy no está seleccionada no aparece.

Para probar: guardar una acción con fecha de ayer y estado Pendiente o En curso → 1 vencida; pasarla a Completada → 0.`,
  },

  kpi_critical_path: {
    title: 'Ruta crítica',
    text: `- Valor: seleccionadas con activities.critical_path >= 1.
- "Sin holgura definida": seleccionadas cuya holgura es NULL.
  La holgura solo existe (= 0) en las de ruta crítica; para el resto P6 no trae holgura total.

Para probar: UPDATE activities SET critical_path = 1 WHERE id = ...`,
  },

  kpi_priority: {
    title: 'Prioridad P1/P2',
    text: `- Valor: seleccionadas con activity_performance_reviews.priority en 'p1' o 'p2'.
- "P1: x · P2: y": el reparto entre las dos.
- Pie "N sin prioridad": seleccionadas sin prioridad asignada.

La prioridad es manual: se elige en la ficha de la actividad (selector Prioridad + check verde).`,
  },

  chart_progress: {
    title: 'Avance a origen',
    text: `Una barra por actividad seleccionada, en % a la fecha de corte.

Presupuestado (línea base) y Planificado (última planificación):
   PlannedExecutionService::getScheduledActivityProgress(actividad, hoy).
   - Si la actividad tiene filas en planned_executions:
     % = Σ quantity con plan_date <= hoy / Σ quantity total × 100.
     Es el mismo valor para línea base y planificado.
   - Si no tiene: % lineal por duración entre {prefijo}_start_date y {prefijo}_end_date.
     Prefijo 'scheduled' para la línea base, 'current' para el planificado.

Real: activities.progress (el avance que ya tiene guardado la actividad).

Para probar:
- Cambiar scheduled_end_date frente a current_end_date (sin planned_executions) → las dos barras se separan.
- Cambiar activities.progress → cambia la barra Real.`,
  },

  chart_staff: {
    title: 'Personas medias a origen',
    text: `Planificado (attachStaffToDate → plannedStaffToDate):
   - Si la actividad padre tiene activities.people > 0: ese valor.
   - Si no: Σ (people × días laborables de cada subactividad dentro de [current_start_date, min(current_end_date, hoy)])
     / días laborables de ese rango del padre.
   NULL si la actividad aún no ha empezado o nadie tiene people.

Real (actualStaffToDate), con las líneas OK de todas las mediciones de la rama que tienen people > 0 y start_date <= hoy:
   Σ people × días laborables [start_date, min(end_date, hoy)] / días laborables [primera start_date, hoy].

Presupuestado: no existe en el sistema; no se dibuja.

Para probar:
- activities.people = 5 en la actividad padre → Planificado = 5.
- Crear líneas OK con people, start_date y end_date en una medición de la actividad → aparece Real.`,
  },

  chart_weeks: {
    title: 'Semanas para terminar',
    text: `Por actividad, con su medición primaria.

Pendiente = total_units − cantidad ejecutada.
Para mediciones Resource, la cantidad ejecutada es assignments.progress (Assignment::getProgressQuantity).

- Ritmo actual (4 sem.): unidades/semana de las líneas OK de la medición con start_date <= hoy, en la ventana [hoy − 27 días, hoy].
  Cada línea se prorratea por días laborables: executed_quantity × días de la línea dentro de la ventana / días de la línea.
  Ritmo = Σ / 4.
  Semanas = pendiente / ritmo.
- Ritmo del plan (6 sem.): total × (% planificado en min(hoy + 6 sem., current_end_date) − % planificado hoy) / 100 / semanas de ese tramo.
  Semanas = pendiente / ritmo del plan.
  Es 0 si current_end_date ya pasó.
- Raya roja: semanas que quedan = (current_end_date − hoy) / 7. Negativo = fin de programa ya pasado.

"Sin ritmo por falta de fecha": la medición tiene ejecutado > 0 pero ninguna línea OK con start_date.

Medición primaria (misma regla que la planificación semanal):
- Un solo BOQ vinculado en activities_boq_roots → la medición con ese boq_root_id.
- Varios BOQ → la del vínculo con is_primary = 1.
- Si no → si la rama tiene una sola medición física (o varias del mismo BOQ), esa.
- Si no → no hay primaria.

Modo subactividad: cada subactividad crea su propia medición desde la BOQ vinculada al padre.
Las mediciones de la rama con el mismo boq_root_id se agrupan en una sola:
- total, ejecutado y líneas se suman;
- el ritmo del plan es la suma del de cada parte, cada una con las fechas (current_*) de su subactividad.
Las subactividades canceladas (status = 6) no entran.

Para probar: crear líneas OK con executed_quantity, start_date y end_date dentro de las últimas 4 semanas en la medición primaria.`,
  },

  attention: {
    title: 'Actividades que requieren atención',
    text: `Una fila por actividad seleccionada, ordenadas por project_delay de mayor a menor (las que no tienen dato, al final).

- Peso presup. %: cost_share (ver Selección).
- LB / Últ. plan / Real: igual que la gráfica "Avance a origen".
  La "F" delante del Real indica que no hay medición primaria o que su total es 0.
- Var.: Real − Últ. plan (puntos porcentuales).
- Medición: select con las mediciones físicas padre de la rama; ★ = primaria.
  Ejecutado, Pendiente, Ritmo 4s, Plan 6s, Retraso bruto y Retraso proyecto cambian con la medición elegida.
- Fin / Sem. hasta fin: current_end_date y (current_end_date − hoy) / 7.
- Holgura: 0 si critical_path >= 1; "—" si no.
- Estado (calculado en el front, situationOf):
  · actual >= 100 y pendiente > 0  → Completada con pendiente
  · actual >= 100                  → Completada
  · fin pasado y pendiente > 0     → Fuera de plazo
  · sin ritmo de 4 semanas         → En curso sin ritmo
  · resto                          → En curso
- Prioridad, Acción, Responsable, Fecha: activity_performance_reviews.

Para probar: cambiar current_end_date a una fecha pasada → "Fuera de plazo" y Sem. hasta fin negativo.`,
  },

  sheet_actual_progress: {
    title: 'Avance real',
    text: `Valor: activities.progress de la actividad.

Debajo:
- LB: % de línea base al corte.
- Últ. plan: % planificado al corte.
Se calculan igual que en la gráfica "Avance a origen" (planned_executions, o lineal por fechas scheduled_* / current_*).`,
  },

  sheet_project_delay: {
    title: 'Retraso proyecto (días)',
    text: `Con la medición elegida en el select:
   retraso bruto = max(0, redondeo((pendiente / ritmo real de 4 sem. − (current_end_date − hoy) / 7) × 7))
   holgura       = 0 si critical_path >= 1; si no, sin dato
   retraso       = retraso bruto − holgura

Sin ritmo real (no hay líneas OK con fecha en las últimas 4 semanas) no hay retraso bruto.`,
  },

  sheet_weeks_to_end: {
    title: 'Semanas hasta fin programa',
    text: `Valor: (current_end_date − hoy) / 7, redondeado a 1 decimal. En rojo si es negativo.

Debajo:
- Fin: current_end_date.
- Pendiente: pendiente de la medición elegida = total_units − assignments.progress.

Para probar: mover current_end_date.`,
  },

  sheet_actual_yield: {
    title: 'Rendimiento real a origen (h/ud)',
    text: `h/ud real = HH registradas / cantidad, solo con las líneas OK de la medición elegida que:
- tienen end_date <= hoy,
- tienen start_date,
- tienen people > 0.

HH de una línea = people × días laborables [start_date, end_date] (mínimo 1) × jornada.
Es el mismo cálculo que "HH realizadas" en la planificación semanal.

Debajo:
- Presup.: no existe en el sistema.
- Planif. (h/ud plan):
  · si la medición tiene man_hour_index > 0: ese índice (HH plan = total_units × man_hour_index);
  · si no, y es la única medición de la actividad o la primaria: HH programadas / total_units,
    con HH programadas = activities.people × días laborables [current_start_date, current_end_date] × jornada
    (si el padre no tiene people, se suman las de sus subactividades);
  · en otro caso: sin dato.

Para probar:
- activities.people = 4 → aparece h/ud plan.
- Crear líneas OK con people = 3, start_date / end_date y executed_quantity → aparece h/ud real.`,
  },

  past: {
    title: 'A pasado (a origen)',
    text: `Avance (%):
- Presup.: % de línea base al corte.
- Planif.: % planificado al corte.
- Real: activities.progress (en rojo si es menor que el planificado).

Personas (media):
- Presup.: no existe.
- Planif.*: personas planificadas a origen (ver "Personas medias a origen").
- Real: personas reales a origen, de las líneas OK con people.

Rendimiento (h/ud):
- Presup.: no existe.
- Planif.: h/ud plan.
- Real: h/ud real.
Ver "Rendimiento real a origen".`,
  },

  future: {
    title: 'A futuro',
    text: `Columnas:
- Presup. restante: no hay presupuesto por semana en el sistema → "—".
- Plan 6 sem.
- Necesario para llegar.
- Real (últimas 4 sem.).

Avance (ud/semana):
- Plan 6 sem.: plan_6w (ver "Semanas para terminar").
- Necesario: pendiente / semanas hasta fin (solo si quedan semanas > 0).
- Real 4 sem.: rate_4w.

Personas:
- Plan 6 sem.: personas planificadas, solo si a la actividad le quedan días laborables.
- Necesario: pendiente × h/ud real de las últimas 4 sem. / (días laborables [hoy, current_end_date] × jornada).
- Real 4 sem.: personas medias de las líneas OK de la rama en [hoy − 27 días, hoy]
  = Σ people × días laborables solapados / días laborables de la ventana.

Rendimiento (h/ud):
- Plan 6 sem.: h/ud plan.
- Necesario: HH que quedan / pendiente.
  HH que quedan = personas planificadas × días laborables que quedan × jornada;
  si no hay personas planificadas: HH programadas − HH registradas.
- Real 4 sem.: HH / cantidad de las líneas OK de la medición con end_date en las últimas 4 semanas (y con people).

Para probar: activities.people > 0 y líneas OK recientes con people, fechas y cantidad.`,
  },

  situation: {
    title: 'Situación, causa y acción',
    text: `Situación: se calcula (mismas reglas que la columna Estado de la tabla de atención); no se guarda.

Causa: texto libre en activity_performance_reviews.cause.
- Aparece el check verde cuando el texto cambia.
- Al pulsarlo hace PUT /api/v2/project/{project}/dashboard-performance/activities/{activity}/review.

Acción acordada: action, action_manager, action_date y action_status de la misma tabla (se editan en el bloque "Acción acordada").

Permisos:
- Leer requiere permiso de lectura del proyecto.
- Guardar requiere permiso update del proyecto (project.can_admin).`,
  },

  weekly_progress: {
    title: 'Avance acumulado · últimas 6 semanas',
    text: `Barras de las 6 semanas del proyecto que terminan en la semana de corte (S = número de semana del proyecto).
Valor en el último día de cada semana; la semana de corte se recorta a week_end.

- Presup.: % de línea base acumulado a ese día.
- Planif.: % planificado acumulado a ese día.
- Real: Σ (executed_quantity / total_units × peso) de las líneas OK con end_date <= ese día.
  Cuenta todas las mediciones físicas de la rama.
  Peso = assignments.weight; si es NULL, amount de la medición / Σ amount de su (sub)actividad × 100.
  Si la medición cuelga de una subactividad, ese peso se multiplica por lo que la subactividad aporta al padre
  (Activity::reCalculateParentProgress): weight de la subactividad / 100 si alguna hermana tiene weight;
  si ninguna lo tiene, sus días laborables / días laborables de todas las hermanas.
  · Las líneas OK sin end_date solo se suman en la semana de corte.
  · Si eso da 0 en la semana de corte se usa activities.progress.
  · Si la rama no tiene ninguna línea fechada, las semanas anteriores salen vacías: no se sabe cuánto había.

Para probar: crear líneas OK con end_date en semanas distintas de las últimas 6 → el Real sube escalonado.`,
  },

  weekly_staff: {
    title: 'Personas · últimas 6 semanas',
    text: `- Planif.:
  · personas planificadas a origen si la actividad tiene días laborables dentro de la semana (rango current_start_date..current_end_date);
  · 0 si no tiene;
  · sin dato si no hay people.
- Real: personas medias de la semana = Σ people × días laborables de cada línea OK dentro de la semana / días laborables de la semana (hasta hoy).
  Son las líneas OK con people > 0 de la rama.
  · 0 si la rama tiene líneas pero ninguna en esa semana;
  · vacío si la rama no tiene líneas con personas.
- Presup.: no existe.

Para probar: líneas OK con people y fechas dentro de alguna de las 6 semanas.`,
  },

  weekly_yield: {
    title: 'Rendimiento · últimas 6 semanas',
    text: `Solo de la medición elegida.

- Planif.: HH programadas de la semana / cantidad planificada de la semana.
  HH = personas planificadas × días laborables de la semana dentro de la actividad × jornada.
  Cantidad = (% planificado al fin de la semana − % planificado el día antes de empezar) / 100 × total_units.
- Real: HH / cantidad de las líneas OK de la medición con end_date dentro de la semana (hasta hoy), solo las que tienen people > 0 y start_date.
  HH de una línea = people × días laborables [start_date, end_date] × jornada.
- Presup.: no existe.

Para probar: activities.people > 0 (Planif.) y líneas OK con people y end_date en la semana (Real).`,
  },

  history_progress: {
    title: 'Avance acumulado por mes de obra',
    text: `Mes de obra 1 = mes de projects.current_start_date.
Se dibujan los meses desde el de current_start_date hasta el de current_end_date de la actividad (máximo 120).

- Presup. / Planif.: % de línea base / planificado acumulado al último día de cada mes.
- Real: igual que el semanal, acumulado hasta fin de mes. En el mes en curso, hasta hoy.
  Meses futuros: vacío.
  Meses pasados sin líneas fechadas: vacío.
- Necesario: rampa lineal por días laborables desde el real de hoy hasta 100 % en current_end_date.
  Punto al fin de cada mes desde el mes en curso:
     real hoy + (100 − real hoy) × días laborables [hoy, fin de mes] / días laborables [hoy, current_end_date].

Para probar: mover current_end_date más lejos → la rampa del Necesario se suaviza.`,
  },

  history_staff: {
    title: 'Personas por mes de obra',
    text: `- Planif.: personas planificadas a origen en los meses con días laborables de la actividad.
- Real: personas medias del mes = Σ people × días laborables de cada línea OK dentro del mes / días laborables del mes (hasta hoy en el mes en curso).
  Son las líneas OK con people > 0 de la rama.
- Necesarias: solo meses futuros. Es el valor constante de "Personas · Necesario para llegar" de la tabla A futuro.
- Presup.: no existe.`,
  },

  history_yield: {
    title: 'Rendimiento por mes de obra',
    text: `Solo de la medición elegida.

- Planif.: personas planificadas × días laborables del mes dentro de la actividad × jornada / cantidad planificada del mes.
  Cantidad planificada del mes = (% planificado a fin de mes − % a fin del mes anterior) / 100 × total_units.
- Real: HH / cantidad de las líneas OK de la medición con end_date en el mes y people > 0 (meses pasados y el actual).
- Necesario: solo meses futuros. Es el valor constante de "Rendimiento · Necesario para llegar" de la tabla A futuro.
- Presup.: no existe.`,
  },

  monthly: {
    title: 'Evolución mensual',
    text: `Mismos meses que el histórico; si hay más de 12, solo los 12 últimos. Medición elegida en el select.

Avance (ud):
- Presup.: (% LB a fin de mes − % LB a fin del mes anterior) / 100 × total_units.
- Planif.: igual con el % planificado.
- Real: Σ executed_quantity de las líneas OK de la medición con end_date dentro del mes. Vacío si no hay ninguna.

Avance acumulado %: Presup. y Real, igual que la gráfica de avance acumulado.

Personas y Rendimiento: igual que las gráficas de personas y rendimiento por mes.

Pérdidas (HH perdidas, estimadas): todavía no hay fuente en el sistema → "—".`,
  },

  system_kpis: {
    title: 'Indicadores del detalle del sistema',
    text: `Sobre la medición marcada en la tabla de mediciones.

- Avance real: activities.progress. El ° indica que no hay medición primaria o que su total es 0.
- Últ. plan / LB: % planificado / % línea base al corte.
- Var. avance: real − último plan.
- Pendiente: total_units − assignments.progress, de la medición.
- Sem. para terminar: pendiente / ritmo real de 4 semanas.
- Holgura total: 0 si critical_path >= 1; si no, sin dato.
- Retraso proyecto: retraso bruto − holgura (ver "Retraso proyecto").
- Personas necesarias: pendiente × h/ud real de 4 sem. / (días laborables que quedan × jornada).

Cabecera:
- Presupuesto: coste de la rama (Σ total_units × unit_price de las mediciones hoja).
- Fin: current_end_date.`,
  },

  system_measurements: {
    title: 'Mediciones de la actividad',
    text: `Todas las mediciones físicas padre de la rama. Cada una se analiza por separado: no se suman unidades distintas.
En modo subactividad, las del mismo recurso BOQ en distintas subactividades se muestran como una sola
("suma de N subactividades"): total, ejecutado, líneas y HH sumados; el plan de cada parte con las fechas de su subactividad.

- Origen: "Presupuesto" si assignments.boq_root_id tiene valor (vinculada a BOQ); "Manual" si no.
- Planificado: total_units.
- Ejecutado al corte: assignments.progress.
- Pendiente: total − ejecutado.
- Avance %: ejecutado / total × 100.
- Ritmo 4s / Plan 6s / Sem. para terminar: ver "Semanas para terminar".
- h/ud real: HH registradas / cantidad de las líneas OK con people y fechas hasta hoy.
- HH prog.:
  · total_units × man_hour_index si el índice existe;
  · si no, HH programadas de la actividad cuando es la única medición o la primaria.
- h/ud plan: HH prog. / total_units.
- Avisos: "sin fecha real" cuando hay ejecutado pero ninguna línea OK con start_date.

Al marcar una fila se elige esa medición en toda la ficha.`,
  },

  system_series: {
    title: 'Evolución de la medición',
    text: `12 semanas del proyecto hasta la de corte y 6 después.
"Mensual" agrupa esas mismas semanas por el mes de su lunes.

- Plan:
  · si la medición tiene filas en weekly_distributions (distributable = la medición): scheduled_quantity de cada week_number;
  · si no: (% planificado a fin de semana − % el día antes de empezar) / 100 × total_units.
- Real: Σ executed_quantity de las líneas OK de la medición con end_date en la semana (hasta hoy).
  Vacío si la medición no tiene líneas fechadas.
- HH registradas: Σ people × días laborables [start_date, end_date] × jornada de esas líneas.
- HH derivadas: Real × h/ud plan.

Para probar:
- Insertar weekly_distributions (distributable_type 'App\\Models\\AssignmentType\\Resource', distributable_id = id de la medición, week_number, scheduled_quantity) → cambia el Plan.
- Crear líneas OK con end_date en esas semanas → aparece el Real.`,
  },

  simulation: {
    title: 'Simulación',
    text: `Cálculo solo en el navegador; no guarda nada. "Restaurar" vuelve a los valores del sistema.

Valores iniciales:
- Pendiente: el de la medición.
- Ritmo próximo: rate_4w.
- Ritmo plan: plan_6w.
- h/ud: real de 4 semanas (o a origen si no hay).
- Holgura y fin de programa: los de la actividad.

Resultados:
- Semanas hasta fin = (fin − hoy) / 7.
- Semanas al ritmo = pendiente / ritmo.
- Retraso bruto = max(0, redondeo((semanas al ritmo − semanas hasta fin) × 7)).
- Retraso proyecto = retraso bruto − holgura.
- Ritmo necesario = pendiente / semanas hasta fin.
- Personas necesarias = ritmo necesario × h/ud / horas por persona y semana.
  Horas por semana = jornada × días marcados en projects.configs.work_days (5 por defecto).`,
  },

  agreed_action: {
    title: 'Acción acordada',
    text: `Se guarda en activity_performance_reviews: una fila por actividad (activity_id único).
Campos: priority, cause, action, action_manager, action_date, action_status.

Endpoint: PUT /api/v2/project/{project}/dashboard-performance/activities/{activity}/review
- Requiere permiso update del proyecto.
- Valida:
  · priority: p1 / p2;
  · cause / action: máximo 2000 caracteres;
  · action_manager: máximo 255;
  · action_date: Y-m-d;
  · action_status: pending / in_progress / completed.
- Si todos los campos llegan vacíos se borra la fila.

Lo usan:
- el indicador "Acciones vencidas";
- las columnas Prioridad, Acción, Responsable y Fecha de la tabla de atención.`,
  },
}
